import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { PAGE_TYPE } from "@faire/retailer-visitor-shared/consts/PAGE_TYPE";
import { ShowroomVettingCriteria } from "@faire/retailer-visitor-shared/features/Showroom/ShowroomVettingCriteria/ShowroomVettingCriteria";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import React from "react";
import styled, { css, keyframes } from "styled-components";
var IMAGE_DELAY_DURATION = 3000;
var IMAGE_DISSOLVE_DURATION = 300;
var getFadeInPercent = function(totalDuration) {
    return "".concat(100 * IMAGE_DISSOLVE_DURATION / totalDuration, "%");
};
var getDelayPercent = function(totalDuration) {
    return "".concat(100 * (IMAGE_DELAY_DURATION + IMAGE_DISSOLVE_DURATION) / totalDuration, "%");
};
var getFadeOutPercent = function(totalDuration) {
    return "".concat(100 * (IMAGE_DELAY_DURATION + 2 * IMAGE_DISSOLVE_DURATION) / totalDuration, "%");
};
export var ShowroomHeaderBanner = function() {
    var _React_useContext = React.useContext(Context), layoutElement = _React_useContext.layoutElement, onCTAClick = _React_useContext.onCTAClick, pageType = _React_useContext.pageType, requestId = _React_useContext.requestId;
    var main_text = layoutElement.main_text, cta_link = layoutElement.cta_link, cta_text = layoutElement.cta_text, images = layoutElement.images, on_click_event_name = layoutElement.on_click_event_name, description_text = layoutElement.description_text;
    var isSSR = useIsSSR();
    if (!main_text || !cta_link || !cta_text) {
        return null;
    }
    var isHomepage = pageType === PAGE_TYPE.HOMEPAGE;
    var numCategory = images.length / 2;
    var totalDuration = (IMAGE_DELAY_DURATION + IMAGE_DISSOLVE_DURATION) * numCategory;
    var bannerInfo = on_click_event_name !== null && on_click_event_name !== void 0 ? on_click_event_name : "ShowroomHeaderBanner";
    var gap = function() {
        return {
            mobileAndAbove: "0",
            tabletAndAbove: "7x",
            desktopAndAbove: "3x",
            xLargeAndAbove: isHomepage ? "3x" : "4x"
        };
    };
    var showroomVettingCriteria = function(showBorder) {
        return /*#__PURE__*/ _jsx(ShowroomVettingCriteria, {
            gap: gap(),
            color: Core.text.inverse,
            border: showBorder,
            column: showBorder,
            alignText: showBorder ? "center" : "left"
        });
    };
    return /*#__PURE__*/ _jsxs(Container, {
        "data-test-id": "showroom-header-banner",
        children: [
            /*#__PURE__*/ _jsxs(LeftContentWrapper, {
                $isHomepage: isHomepage,
                children: [
                    /*#__PURE__*/ _jsxs(TextAndButtonWrapper, {
                        $isHomepage: isHomepage,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                color: Core.text.inverse,
                                variant: "paragraphSansRegular",
                                children: description_text
                            }),
                            /*#__PURE__*/ _jsx(StyledMainText, {
                                variant: {
                                    desktopAndAbove: "displayLSerifRegular",
                                    mobileAndAbove: "displayMSerifRegular"
                                },
                                children: main_text
                            }),
                            /*#__PURE__*/ _jsx(StyledButton, {
                                linkTo: cta_link,
                                variant: "secondary",
                                onClick: function() {
                                    onCTAClick === null || onCTAClick === void 0 ? void 0 : onCTAClick(bannerInfo, requestId);
                                },
                                children: cta_text
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ShowroomVettingCriteriaWrapper, {
                        $isHomepage: isHomepage,
                        children: /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                            mobile: showroomVettingCriteria(false),
                            tabletAndAbove: showroomVettingCriteria(true),
                            xLargeAndAbove: isHomepage ? showroomVettingCriteria(true) : showroomVettingCriteria(false)
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ImageContainer, {
                children: images.map(function(image, index) {
                    return /*#__PURE__*/ _jsx(StyledImage, {
                        $left: index % 2 ? "50%" : "0",
                        $order: Math.floor(index / 2),
                        $animated: numCategory > 1,
                        $fadeInPercent: getFadeInPercent(totalDuration),
                        $fadeOutPercent: getFadeOutPercent(totalDuration),
                        $delayPercent: getDelayPercent(totalDuration),
                        $delay: IMAGE_DELAY_DURATION,
                        $dissolve: IMAGE_DISSOLVE_DURATION,
                        $total: totalDuration,
                        src: optimizeImageUrl(image, isSSR)
                    }, "showroom-normal-category-header-banner-".concat(index));
                })
            })
        ]
    });
};
var Container = styled(Flex).attrs({
    direction: {
        mobileAndAbove: "column",
        desktopAndAbove: "row"
    },
    justify: {
        mobileAndAbove: "normal",
        xLargeAndAbove: "flex-end"
    }
}).withConfig({
    componentId: "sc-c5cc86ee-0"
})([
    "position:relative;background:",
    ";width:100%;overflow:hidden;z-index:1;"
], Core.surface.enabled);
var LeftContentWrapper = styled(Column).attrs({
    align: {
        mobileAndAbove: "center",
        desktopAndAbove: "flex-start"
    }
}).withConfig({
    componentId: "sc-c5cc86ee-1"
})([
    "width:100%;height:fit-content;padding:",
    ";",
    "{padding-bottom:",
    ";}",
    "{width:50%;padding-top:",
    ";}",
    "{padding-top:",
    ";max-width:640px;}"
], getSpacing("7x", 0, "6x"), responsiveMediaQueries.tabletAndAbove, getSpacing("3x"), responsiveMediaQueries.desktopAndAbove, function(props) {
    return getSpacing(props.$isHomepage ? "7x" : "9x");
}, responsiveMediaQueries.xLargeAndAbove, function(props) {
    return getSpacing(props.$isHomepage ? "7x" : "10x");
});
var TextAndButtonWrapper = styled(Column).attrs({
    align: {
        mobileAndAbove: "center",
        desktopAndAbove: "flex-start"
    }
}).withConfig({
    componentId: "sc-c5cc86ee-2"
})([
    "",
    "{margin:0 auto;}",
    "{padding-left:",
    ";margin:0;}"
], responsiveMediaQueries.desktopAndAbove, responsiveMediaQueries.xLargeAndAbove, function(props) {
    return getSpacing(props.$isHomepage ? "5x" : 0);
});
var ImageContainer = styled(Row).withConfig({
    componentId: "sc-c5cc86ee-3"
})([
    "width:100%;padding-top:75%;position:relative;overflow:hidden;",
    "{width:50%;padding:0;}"
], responsiveMediaQueries.desktopAndAbove);
var fade = function(fadeIn, delay, fadeOut) {
    return keyframes([
        "0%{opacity:0;};",
        "{opacity:1;}",
        "{opacity:1;}",
        "{opacity:0;}100%{opacity:0;}"
    ], fadeIn, delay, fadeOut);
};
var StyledImage = styled.img.withConfig({
    componentId: "sc-c5cc86ee-4"
})([
    "position:absolute;width:50%;top:0;left:",
    ";height:auto;min-height:100%;object-fit:cover;",
    ";"
], function(props) {
    return props.$left;
}, function(props) {
    return props.$animated ? css([
        "animation:",
        " ",
        "ms ease-out ",
        "ms infinite;opacity:0;"
    ], fade(props.$fadeInPercent, props.$delayPercent, props.$fadeOutPercent), props.$total, props.$order * (props.$delay + props.$dissolve)) : "";
});
var StyledMainText = styled(Typography).attrs({
    color: Core.text.inverse
}).withConfig({
    componentId: "sc-c5cc86ee-5"
})([
    "&&&{padding:",
    ";text-align:center;max-width:464px;",
    "{padding:",
    ";}",
    "{text-align:start;}}"
], getSpacing(0, "1x", "3x"), responsiveMediaQueries.tabletAndAbove, getSpacing(0, 0, "4x"), responsiveMediaQueries.desktopAndAbove);
var StyledButton = styled(Button).withConfig({
    componentId: "sc-c5cc86ee-6"
})([
    "&&&{padding:",
    ";width:200px;&:active,&:focus,&:hover{background-color:",
    ";color:",
    ";}}"
], getSpacing("2x"), Core.surface.inverse, Core.text.default);
var ShowroomVettingCriteriaWrapper = styled(Flex).attrs({
    align: "center",
    direction: {
        mobileAndAbove: "column",
        tabletAndAbove: "row"
    }
}).withConfig({
    componentId: "sc-c5cc86ee-7"
})([
    "margin:auto;padding-top:",
    ";row-gap:",
    ";",
    "{padding:",
    ";}",
    "{padding-left:0;padding-top:",
    ";}",
    "{padding-top:",
    ";margin-left:",
    ";}"
], getSpacing("6x"), getSpacing("3x"), responsiveMediaQueries.tabletAndAbove, getSpacing("8x", 0, 0, "6x"), responsiveMediaQueries.desktopAndAbove, function(props) {
    return getSpacing(props.$isHomepage ? "5x" : "8x");
}, responsiveMediaQueries.xLargeAndAbove, function(props) {
    return getSpacing(props.$isHomepage ? "5x" : "10x");
}, function(props) {
    return getSpacing(props.$isHomepage ? "2x" : "-4x");
});
